import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import refreshIconBlue from '@coursera/assets/icons/refresh-blue.svg';
import refreshIcon from '@coursera/assets/icons/refresh.svg';

import matchMobile from '../helpers/util/match-mobile';
import { Button } from '../index';

import ExampleModal from './example-modal';
import styles from './flash-cards.module.scss';

const cx = classnames.bind(styles);

export function CardSections({ side }) {
  return (
    side &&
    side.map((section) => (
      <div className={styles.section} key={section}>
        <div className={styles.label}>{section.label}</div>
        <p className={styles[section.className]} style={section.style}>
          {section.content}
        </p>
      </div>
    ))
  );
}

CardSections.propTypes = {
  side: PropTypes.array.isRequired,
};

export function Card({
  cardFlip,
  data,
  isCurrentCard,
  lastCard,
  navigation,
  onExpand,
  onFlip,
  onNext,
  sectionOrder,
  ...rest
}) {
  const [isFlipped, setIsFlipped] = useState(null);
  const [isExpanded, setIsExpanded] = useState(data.expanded);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLastCard, setIsLastCard] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const toggleExpand = useCallback(
    (state) => {
      setIsExpanded(state);
      onExpand(state && (isMobile ? data.mobileHeight : data.maxHeight));
    },
    [data, isMobile, onExpand],
  );

  const flipCard = (flip) => {
    toggleExpand(flip);
    setIsFlipped(flip);
    onFlip();
  };

  useEffect(() => {
    if (isCurrentCard) {
      toggleExpand(data.expanded);
    } else {
      data.expanded = false;
      setIsExpanded(false);
      setIsFlipped(false);
    }
  }, [data, isCurrentCard, toggleExpand]);

  useEffect(() => {
    if (!cardFlip) {
      return;
    }

    if (isFlipped) {
      setIsFlipped(null);
    }
  }, [cardFlip, isFlipped]);

  useEffect(() => {
    setIsLastCard(lastCard);
  }, [lastCard]);

  useEffect(() => {
    setIsMobile(matchMobile.isMobile);

    const handleMediaChange = (matches) => {
      setIsMobile(matches);
    };

    matchMobile.addListener(handleMediaChange);

    return () => matchMobile.removeListener(handleMediaChange);
  }, []);

  return (
    <div
      key={data}
      className={cx({
        card: true,
        flipped: isFlipped,
        current: isCurrentCard,
      })}
      style={
        isExpanded && isFlipped ? { height: isMobile ? data.mobileHeight : data.maxHeight } : {}
      }
      {...rest}
    >
      <div className={styles.front}>
        <div className={styles.content} aria-hidden={isFlipped || isCurrentCard === false}>
          <div className={styles.desc} aria-live="polite">
            <CardSections side={data.front} />
          </div>
          {!isFlipped && (
            <Button
              variant="white"
              onClick={() => flipCard(true)}
              tabIndex={isCurrentCard ? '0' : '-1'}
            >
              <img src={refreshIconBlue} alt="" />
              Flip
            </Button>
          )}
        </div>
        {!isFlipped && (
          <div className={cx({ footer: true, notCurrent: isCurrentCard === false })}>
            {navigation}
          </div>
        )}
      </div>

      <div className={styles.back}>
        <div className={styles.content} aria-hidden={!isFlipped || isCurrentCard === false}>
          <div className={styles.desc} aria-live="polite">
            {sectionOrder.map((sectionName) => (
              <CardSections key={sectionName} side={data[sectionName]} />
            ))}
          </div>
          {isFlipped && (
            <div className={cx({ ['button-group']: true, ['last-card']: isLastCard })}>
              {!isLastCard && (
                <Button
                  onClick={onNext}
                  tabIndex={isCurrentCard && isFlipped ? '0' : '-1'}
                  variant="secondary"
                >
                  Next card
                </Button>
              )}
              <Button
                onClick={() => flipCard(null)}
                tabIndex={isCurrentCard && isFlipped ? '0' : '-1'}
              >
                <img src={refreshIcon} alt="" />
                Flip
              </Button>
            </div>
          )}
          {data.expand && (
            <>
              <div className={styles.trigger}>
                <Button onClick={() => setIsModalOpen(true)} variant="outline" size="small">
                  SHOW EXAMPLE
                </Button>
              </div>
              <div className={styles.expand}>
                <div className={styles.toggle}>
                  <button
                    onClick={() => toggleExpand(!isExpanded)}
                    tabIndex={isCurrentCard ? '0' : '-1'}
                    aria-expanded={isExpanded}
                    aria-controls={`panel-${data.id}`}
                  >
                    {isExpanded ? 'Hide' : 'Show'} example
                  </button>
                </div>
                <div
                  className={cx({ 'expand-content': true, show: isExpanded })}
                  id={`panel-${data.id}`}
                  aria-label={`${data.front[0].content} Example`}
                  role="region"
                >
                  {data.image && <img src={data.image} alt="Chart" />}
                  <CardSections side={data.expand} />
                </div>
              </div>
              <ExampleModal
                image={data.mobileImage}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sections={data.expand}
              />
            </>
          )}
        </div>
        {isFlipped && <div className={styles.footer}>{navigation}</div>}
      </div>
    </div>
  );
}

Card.propTypes = {
  cardFlip: PropTypes.bool,
  data: PropTypes.any.isRequired,
  isCurrentCard: PropTypes.bool,
  lastCard: PropTypes.bool,
  navigation: PropTypes.node,
  onExpand: PropTypes.func,
  onFlip: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  sectionOrder: PropTypes.arrayOf(PropTypes.string),
};

Card.defaultProps = {
  onFlip: () => {},
  sectionOrder: ['front', 'back'],
};
